import noop from '@anm/helpers/noop';
import { longBorderDashesStyles, stretchBlock } from '@anm/styles/helpers';
import { useCallback, useEffect, useState, DragEvent, PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';

import payloadObserver from '../payloadObserver';

export type DropZoneProps<T> = {
  className?: string;
  canShowDropZone?: boolean;
  canDrop?: (payload: T | null) => boolean;
  onDropped?: (payload: T | null, e: DragEvent<HTMLDivElement>) => void;
  onDragOver?(evt: DragEvent<HTMLDivElement>): void;
  onDragLeave?(evt: DragEvent<HTMLDivElement>): void;
};

type DropZoneBGProps = {
  isDraggingOver: boolean;
};

const dragOverStyle = css`
  border: 1px solid #0094b5;
  background: rgba(0, 154, 255, 0.2);
`;

export const DropZoneBG = styled.div<DropZoneBGProps>`
  ${stretchBlock}
  ${longBorderDashesStyles};
  transform: scale(1.11, 1.085);
  border-radius: 10px;
  background-color: rgba(0, 154, 255, 0.1);
  transition: border, background-color 0.3s ease-out;

  ${({ isDraggingOver }) => isDraggingOver && dragOverStyle};
`;

export const DropZoneWrapper = styled.div`
  position: relative;
`;

export const useCurrentPayload = <T extends {}>() => {
  const [payload, setPayload] = useState<T | null>(null);

  useEffect(() => {
    const subscriber = (payload: T | null) => {
      setPayload(payload);
    };

    const unsubscribe = payloadObserver.subscribe(subscriber);
    return () => {
      unsubscribe();
    };
  }, [payload]);

  return { payload };
};

const DropZone = <T extends {}>({
  children,
  className,
  canDrop,
  canShowDropZone,
  onDropped = noop,
  onDragOver = noop,
  onDragLeave = noop
}: PropsWithChildren<DropZoneProps<T>>) => {
  const [isDraggingOver, setDraggingOver] = useState(false);
  const { payload } = useCurrentPayload<T>();

  useEffect(() => {
    !canShowDropZone && setDraggingOver(false);
  }, [canShowDropZone]);

  const canDropWrapper = () => canShowDropZone && canDrop?.(payload);

  const handleDragLeave = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      onDragLeave(e);
      setDraggingOver(false);
    },
    [onDragLeave]
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDraggingOver(true);
      onDragOver(e);
    },
    [onDragOver]
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!payload && !canDropWrapper()) return;

      if (e.preventDefault) e.preventDefault();
      if (e.stopPropagation) e.stopPropagation();

      onDropped(payload, e);
    },
    [payload, canDropWrapper(), onDropped]
  );

  return (
    <DropZoneWrapper onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
      {canDropWrapper() && <DropZoneBG data-id="dropzone-bg" {...{ className, isDraggingOver }} />}
      {children}
    </DropZoneWrapper>
  );
};

export default DropZone;
